/// <reference types="@types/gtag.js" />
import { posthog } from "posthog-js";
import {
  gaClientIdProp,
  gaSessionIdProp,
  googleAdsSignupConversion,
  googleAnalyticsMeasurementId,
} from "../../constants.ts";
import {
  type CheckoutAttribution,
  checkoutAttributionSchema,
} from "../../checkoutAttribution.ts";

declare global {
  interface Window {
    fbq?: (
      action: string,
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

export const trackMetaPixelEvent = (
  eventName: string,
  params?: Record<string, unknown>,
) => {
  const win = typeof window !== "undefined"
    ? window
    : (globalThis as unknown as Window);
  if (typeof win.fbq === "function") {
    win.fbq("track", eventName, params);
  }
};

export const captureGoogleAnalyticsIds = () => {
  if (typeof gtag === "undefined") {
    console.error("gtag is not defined - Google Analytics script not loaded");
    return;
  }

  gtag("get", googleAnalyticsMeasurementId, "client_id", (clientId) => {
    if (clientId) {
      posthog.setPersonProperties({ [gaClientIdProp]: clientId });
    }
  });

  gtag("get", googleAnalyticsMeasurementId, "session_id", (sessionId) => {
    if (sessionId) {
      posthog.setPersonProperties({ [gaSessionIdProp]: sessionId });
    }
  });
};

const getGoogleId = (field: "client_id" | "session_id") =>
  new Promise<unknown>((resolve) => {
    const timeout = setTimeout(() => resolve(undefined), 1000);
    gtag("get", googleAnalyticsMeasurementId, field, (value) => {
      clearTimeout(timeout);
      resolve(value);
    });
  });

// The CMP must supply its current choice; missing consent or blocked tags must
// never block checkout or create a synthetic analytics identity.
export const getCheckoutAttribution = async (
  consent: Pick<CheckoutAttribution, "adUserData" | "adPersonalization"> & {
    analyticsConsent: boolean;
  },
): Promise<CheckoutAttribution | undefined> => {
  if (!consent.analyticsConsent || typeof gtag === "undefined") return;
  const [clientId, sessionId] = await Promise.all([
    getGoogleId("client_id"),
    getGoogleId("session_id"),
  ]);
  const result = checkoutAttributionSchema.safeParse({
    ...consent,
    clientId,
    sessionId: Number(sessionId),
    capturedAt: Date.now(),
  });
  return result.success ? result.data : undefined;
};

export const trackSignUpConversion = (method: string = "email") => {
  if (typeof gtag !== "undefined") {
    gtag("event", "sign_up", { method });
    gtag("event", "conversion", {
      send_to: googleAdsSignupConversion,
      event_category: "engagement",
    });
  }

  trackMetaPixelEvent("CompleteRegistration", {
    content_name: method,
    status: true,
  });

  posthog.capture("User Signed Up", { method });
};

export const trackLeadConversion = (channel: string) => {
  if (typeof gtag !== "undefined") {
    gtag("event", "generate_lead", { channel });
  }

  trackMetaPixelEvent("Lead", {
    content_name: channel,
  });

  posthog.capture("Channel Connected", { channel });
};
